import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { CustomButton } from "@/components/Button";
import { textStyles } from "@/designTokens";
import { useObservedHeight } from "@/hooks/useObservedHeight";
import theme from "@/theme";

import { RegistrationErrorSummary } from "./RegistrationActions";

const useStyles = makeStyles(() => ({
  notice: { padding: 16, borderRadius: 8, backgroundColor: theme.palette.semantic["status/info-bg"], marginBottom: 24 },
  noticeTitle: { ...textStyles["SP/Heading/H4"], marginBottom: 8 },
  text: { ...textStyles["SP/Body/Small"], color: theme.palette.text.secondary },
  section: { marginBottom: 32 },
  title: { ...textStyles["SP/Heading/H2"], marginBottom: 8 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderBottom: `1px solid ${theme.palette.semantic["border/default"]}`,
    padding: "12px 0",
    minHeight: 49,
  },
  label: { ...textStyles["SP/Body/Small"], flex: 1, minWidth: 0, color: theme.palette.text.secondary },
  value: { ...textStyles["SP/Body/Large"], overflowWrap: "anywhere", maxWidth: "48%", textAlign: "right" },
  ai: {
    color: theme.palette.semantic["accent/text"],
    backgroundColor: theme.palette.semantic["accent/bg"],
    padding: "4px 8px",
    borderRadius: 4,
    ...textStyles["SP/Heading/H5"],
  },
  footer: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    padding: "16px 24px calc(24px + env(safe-area-inset-bottom, 0px))",
    backgroundColor: theme.palette.semantic["surface/card"],
    boxShadow: "0 -2px 8px rgba(22,32,46,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxHeight: "80vh",
    overflowY: "auto",
    [theme.breakpoints.up("md")]: { maxWidth: 560, margin: "0 auto", padding: "16px 40px 24px" },
  },
}));

/** 表示は現在のフォーム値を読む。補完値の丸め・再計算・既定値追加は行わない。 */
export const RegistrationReview: React.FC<{ consideration?: boolean; onEdit: (step: number) => void }> = ({
  consideration,
  onEdit,
}) => {
  const classes = useStyles();
  const { control, getValues } = useFormContext();
  // RHF v6のuseWatchは初回だけuseFormのdefaultValuesを返す。
  // AI推定後にマウントするため、setValue済みの現在値を初回の値として渡す。
  const values = useWatch({ control, defaultValue: getValues() });
  const { ref: footerRef, height: footerHeight } = useObservedHeight<HTMLDivElement>({ initialHeight: 140 });
  const groups: { title: string; fields: [string, string, string][] }[] = [
    {
      title: "価格・ローン",
      fields: [
        ["total_price_of_property", consideration ? "販売価格（税込）" : "購入価格（税込）", "万円"],
        ["loan_amount", consideration ? "借入予定額" : "ローン借入金額", "万円"],
        ["loan_interest_rate_percentage", "借入利率（年利）", "%"],
        ["payment_period", "返済年数", "年"],
      ],
    },
    {
      title: consideration ? "買った場合の毎月の収支" : "毎月の収支",
      fields: [
        ["monthly_fee", consideration ? "想定家賃" : "家賃収入", "円"],
        ["monthly_management_fee", consideration ? "想定管理費" : "管理費収入", "円"],
        ["building_management_cost", "建物管理費", "円"],
        ["repair_fund_cost", "修繕積立金", "円"],
        ["management_cost", "賃貸管理費", "円"],
      ],
    },
  ];
  return (
    <Box data-registration-review style={{ paddingBottom: footerHeight }}>
      <Box className={classes.notice}>
        <Typography className={classes.noticeTitle}>AIが入力しました</Typography>
        <Typography className={classes.text}>
          周辺の相場から推定した{consideration ? "想定" : ""}値です。
          <br />
          このまま修正することも可能です。
        </Typography>
      </Box>
      {groups.map((group) => (
        <Box key={group.title} className={classes.section}>
          <Typography component="h2" className={classes.title}>
            {group.title}
          </Typography>
          {group.fields.map(([key, label, unit]) => {
            const value = values[key];
            const filled = value !== undefined && value !== null && value !== "";
            const numeric = Number(String(value ?? "").replace(/,/g, ""));
            return (
              <Box key={key} className={classes.row} data-review-field={key}>
                <span className={classes.label}>{label}</span>
                {filled && <span className={classes.ai}>AI</span>}
                <span className={classes.value}>
                  {filled
                    ? `${
                        Number.isFinite(numeric)
                          ? (unit === "万円" ? numeric / 10000 : numeric).toLocaleString("ja-JP", {
                              minimumFractionDigits: unit === "%" ? 2 : 0,
                              maximumFractionDigits: 10,
                            })
                          : value
                      }${unit}`
                    : "未入力"}
                </span>
              </Box>
            );
          })}
        </Box>
      ))}
      <div ref={footerRef} className={classes.footer} data-registration-review-actions>
        <RegistrationErrorSummary onSelect={onEdit} />
        <CustomButton type="submit" buttonSize="L" fullWidth>
          この内容で登録する
        </CustomButton>
        <CustomButton type="button" tone="text" buttonSize="M" fullWidth onClick={() => onEdit(1)}
          style={{ color: theme.palette.semantic["action/primary"] }}>
          自分で直す
        </CustomButton>
      </div>
    </Box>
  );
};
